import { ApiError } from './client.js';
/**
 * Field names as the forms label them.
 *
 * Keyed by the LAST segment of the path, so an issue on `targets.3.regionId`
 * finds "Distributor" — the index belongs in the path prefix, not the label.
 * Names
 * come from the request schemas in `@fieldvision/contracts`; a field with no
 * entry falls back to its raw path, which is ugly but never wrong, and is the
 * signal that a new field needs a line here.
 */
const FIELD_LABELS = {
    // Identity — auth.ts
    name: 'Nama',
    nik: 'NIK',
    email: 'Email',
    phone: 'Nomor HP',
    password: 'Kata sandi',
    currentPassword: 'Kata sandi saat ini',
    newPassword: 'Kata sandi baru',
    roleId: 'Peran',
    allRegions: 'Akses seluruh Distributor',
    regionIds: 'Distributor',
    code: 'Kode',
    description: 'Deskripsi',
    sortOrder: 'Urutan',
    permissions: 'Izin',
    active: 'Status',
    reassignToRoleId: 'Peran pengganti',
    // Master data — master.ts
    regionId: 'Distributor',
    channel: 'Channel',
    address: 'Alamat',
    deliveryAddress: 'Alamat Kirim',
    lat: 'Lintang',
    lng: 'Bujur',
    gpsToleranceM: 'Toleransi GPS',
    type: 'Tipe',
    competitorId: 'Kompetitor',
    categoryId: 'Kategori',
    targetPriceIdr: 'Harga acuan',
    targetFacing: 'Target facing',
    targetSequence: 'Target urutan rak',
    targetRow: 'Target baris rak',
    groupingId: 'Grouping',
    division: 'Divisi',
    salesUserId: 'Sales',
    periodMonth: 'Periode',
    targetVisits: 'Target kunjungan',
    targetPlanogramScore: 'Target skor planogram',
    targets: 'Target',
    note: 'Catatan',
    internalSkuId: 'SKU internal',
    competitorSkuId: 'SKU kompetitor',
    targetIndex: 'Indeks target',
    competitionIndexThreshold: 'Ambang penilaian',
    // Fraud queue — fraud.ts
    flagId: 'Temuan',
    disposition: 'Keputusan',
};
/** zod 3 messages that are fixed strings. */
const EXACT_MESSAGES = {
    Required: 'wajib diisi',
    'Invalid email': 'format email tidak valid',
    'Invalid uuid': 'pilihan tidak valid',
    'Invalid url': 'format tautan tidak valid',
    'Invalid date': 'tanggal tidak valid',
    'Invalid input': 'nilai tidak valid',
    'Expected number, received nan': 'harus berupa angka',
    Invalid: 'format tidak sesuai',
};
/** zod 3 messages that carry the bound they were built from. */
const MESSAGE_PATTERNS = [
    [/^String must contain at least (\d+) character\(s\)$/, (m) => `minimal ${m[1]} karakter`],
    [/^String must contain at most (\d+) character\(s\)$/, (m) => `maksimal ${m[1]} karakter`],
    [/^String must contain exactly (\d+) character\(s\)$/, (m) => `harus tepat ${m[1]} karakter`],
    [/^Array must contain at least (\d+) element\(s\)$/, (m) => `pilih minimal ${m[1]}`],
    [/^Array must contain at most (\d+) element\(s\)$/, (m) => `pilih maksimal ${m[1]}`],
    [/^Array must contain exactly (\d+) element\(s\)$/, (m) => `pilih tepat ${m[1]}`],
    [/^Number must be greater than or equal to (-?[\d.]+)$/, (m) => `minimal ${m[1]}`],
    [/^Number must be less than or equal to (-?[\d.]+)$/, (m) => `maksimal ${m[1]}`],
    [/^Number must be greater than (-?[\d.]+)$/, (m) => `harus lebih besar dari ${m[1]}`],
    [/^Number must be less than (-?[\d.]+)$/, (m) => `harus lebih kecil dari ${m[1]}`],
    [/^Expected integer, received float$/, () => 'harus bilangan bulat'],
    // The received value is deliberately not echoed: it is the user's own input
    // coming back through the response body, and it belongs in the field, not in
    // a sentence rendered as HTML.
    [/^Invalid enum value\./, () => 'pilihan tidak valid'],
    [/^Invalid literal value/, () => 'nilai tidak valid'],
    [/^Expected .+, received .+$/, () => 'tipe nilai tidak sesuai'],
    [/^Unrecognized key\(s\) in object/, () => 'ada isian yang tidak dikenali'],
];
function translate(message) {
    const exact = EXACT_MESSAGES[message];
    if (exact !== undefined)
        return exact;
    for (const [pattern, render] of MESSAGE_PATTERNS) {
        const match = message.match(pattern);
        if (match)
            return render(match);
    }
    return message;
}
function labelFor(path) {
    const last = path.split('.').at(-1) ?? path;
    return FIELD_LABELS[last] ?? path;
}
/**
 * The issues carried by a 400 from `ZodValidationPipe`, or an empty array.
 *
 * Defensive about the body's shape rather than trusting it: this runs on an
 * error path, and a helper that throws while formatting an error replaces a bad
 * message with a blank screen.
 */
export function apiIssues(error) {
    if (!(error instanceof ApiError))
        return [];
    const body = error.body;
    if (typeof body !== 'object' || body === null || !('issues' in body))
        return [];
    const issues = body.issues;
    if (!Array.isArray(issues))
        return [];
    return issues.flatMap((issue) => {
        if (typeof issue !== 'object' || issue === null)
            return [];
        const { path, message } = issue;
        if (typeof message !== 'string' || message === '')
            return [];
        /*
         * A refinement on the object itself has an empty path — it is about the
         * request as a whole and gets no field label.
         *
         * The pipe sends the path pre-joined, but an array is accepted too: that is
         * zod's own shape, and this helper is one `.join()` being dropped from the
         * server away from labelling every field `undefined`.
         */
        const key = typeof path === 'string' ? path : Array.isArray(path) ? path.join('.') : '';
        return [{ path: key, label: key === '' ? '' : labelFor(key), message: translate(message) }];
    });
}
/**
 * What a banner shows for a failed request — the one function pages call.
 *
 * A validation failure becomes the fields that failed, named. Anything else is
 * already a sentence the API wrote for a human (`a role with the code X already
 * exists`) and is passed through. A non-`ApiError` means fetch itself rejected:
 * there was no response, so there is no server message to show and the honest
 * report is that the network is the problem.
 */
export function apiErrorMessage(error) {
    if (error === null || error === undefined)
        return '';
    const issues = apiIssues(error);
    if (issues.length > 0) {
        return issues
            .map((issue) => (issue.label === '' ? issue.message : `${issue.label}: ${issue.message}`))
            .join('. ');
    }
    if (error instanceof ApiError)
        return error.message;
    // Not an `ApiError` means `fetch` itself rejected — no response, so nothing to
    // report but the connection. Its own message is `Failed to fetch`, which is
    // both English and a lie about whose fault it is.
    return 'Tidak dapat terhubung ke server. Periksa koneksi Anda.';
}
//# sourceMappingURL=validation-errors.js.map